import React, { useState, forwardRef, useImperativeHandle, useEffect, useMemo, useRef } from 'react'
import { Form, Row, Col, Collapse, Tooltip, Descriptions, Table, Button, Space, Message, Tabs, Divider, Modal, Dropdown, Menu, Tag, Radio } from '@bedrock/components'
import { Delete, PlusCircle, Plus, NoticeTextRight, ChevronDown, SignIn, SignOut, Download, CopyNewAdd } from '@bedrock/icons-react'
import { isEqual } from "lodash";
import { useControllableValue, useMemoizedFn } from "ahooks";
import classNames from "classnames/bind";
import styles from "./index.less";
import KPITable, { KPITableProps } from "#features/KPITable";
import SFromItem from "#components/SForm/SFormItem";
import CollapseTablePanel from "#components/CollapseTablePanel";
import FormItemDescriptions, {
    FormItemDescriptionsItemType
} from "#components/DescriptionsFormItem";
import {
    commonGetDemandCooperateProperties,
    PropertyRefDto,
    DemandCooperatePropertyValueDto,
    DemandKpiValueDto,
    commonFilterOptions,
    commonGetDemandCooperateProperties2,
    DemandCooperatePropertiesVo
} from "@mpc/common/src/services/api";
import { useImportFileModal } from "#features/importFileModal";
import CooperationPropertyForm, { alignPropertyValues } from "#features/CooperationFormList/CooperationPropertyForm";
import OvertimeTag from '#features/OvertimeTag';
import { orderNum } from "#utils/number";
import ActualExecutionTag from '#features/ActualExecutionTag';
import PriceDetailModal from './PriceDetailModal';
import CooperationDetails from './CooperationDetails';
import CurrencyType from '#components/SForm/SFormElement/CurrencyType';
import { $sType } from '#utils';
import EquityFormTable, { getExpectRightsLabel, getFormItemKey } from '@mpc/common/src/features/EquityFormTable';
import { cloneDeep } from 'lodash';
import TravelTag from '#features/TravelTag';
import { getInnerStructuredSersion } from '#features/utils';
import ClosingReportBtn from '#features/MPCDemand/ClosingReportTable/ClosingReportBtn';
import NoStructuredCooperation from './NoStructuredCooperation';
import ExportFilePollingBtn from '#features/ExportFilePollingBtn';
import StarSelectDrawer from '#features/MPCDemand/StarSelectDrawer';
import { withFunctionCall } from '@mpc/components';
import { ALLOW_SELECT_KOL_CATEGORY_ID, getCooperateAIRequired } from '#utils/CONST';
import AuditException from '#features/AuditException';
import { bindAiReviewResultToFormItem } from '#features/MPCDemand';
import DropdownListApplyToTheWholeOrder from './DropdownListApplyToTheWholeOrder';
import { applyValuesToRows } from './applyToWholeOrder';
import { readPinnedCodes, savePinnedCodes } from './pinnedColumnsStorage';
import { ADD_ROW_RELEASE_TIME_DELAY_MS, fillReleaseTimeAfterInit } from './releaseTime';
import CooperationTableList, { type CooperationTableListHandle } from './CooperationTableList';

const cx = classNames.bind(styles)

const DEFAULT_ACTIVE_KEYS = Array.from({length: 20}, (_, i) => i);
const ALL_ACTIVE_KEYS = Array.from({length: 1000}, (_, i) => i);

export type CooperationValueItem = {
    id?: number;
    /** 备注 */
    remark?: string;
    /** 是否实际执行 */
    need_actual_execution: boolean;
    /**
     * 是否需包含AI。
     * ⚠️ 值域为 **boolean**（后端契约 `need_ai?: boolean`，见 services/api.ts），
     * 与折叠面板 radio 的 options value 一致；表格模式曾误用 'YES'/'NO' 字符串。
     */
    need_ai?: boolean;
    /** 【结构化】合作内容属性值 */
    property_values?: DemandCooperatePropertyValueDto[];
    /** 【结构化】kpi指标列表 */
    kpi_values?: DemandKpiValueDto[];
    /** 是否包含含差旅餐饮等实报实销 */
    contain_travel_expenses_reimbursement?: boolean;
}

type CooperationFormListProps = {
    category_id: number; // 需求ID
    hasKPI?: boolean; // 是否有KPI
    view?: boolean; // 是否是编辑类型
    value?: CooperationValueItem[];
    onChange?: (value: any[]) => void;
    kpiTableProps?: KPITableProps; // KPI属性
    depth?: number;
    inPlanning: boolean; // 是否为策划信息
    addable?: boolean; // 是否可添加
    deletable?: boolean; // 是否可删除
    readonly?: boolean; // 是否只读,强制所有字段只读，不分场景
    /**
     * 编辑类型
     * default: 默认编辑
     * returnToBusiness: 退回商务确认报价节点（策划编辑）
     */
    editType?: 'default' | 'returnToBusiness'; // 编辑类型
    demand_supplier_quotes?: any
    detail?: any;
    onImportSuccess?: () => void;
    schema?: any
    tabsModel?: boolean; // 是否是tabs模式
    updateReleaseTime?: (values: any, add?: boolean) => void; // 更新发布时间
    /**
     * 顶层「执行日期」(`project_plan_info.$begin_and_end_at`)——**可选取值**，仅用于「初始化后一次性补填」。
     *
     * `updateReleaseTime` 是**只写不读**的回调（它自己去外层 form 取日期），本组件拿不到日期本身；
     * 而新建拆单的第一条合作内容是 `NewCooperate` 兜底造的 `[{}]` 空壳行（连属性集都没有），
     * 不在 `planInfo` 里 → 所有写 `planInfo` 的路径都够不到它。故这里补一个「值」入口，
     * 由本组件就地「补属性集 + 填空」。**不传则完全不启用**（其它调用点零影响）。
     */
    releaseTime?: (string | undefined)[];
    /* 展示模式：panel=折叠面板（默认，零回归），table=表格模式（showModel 切换） */
    showModel?: 'panel' | 'table';
    /**
     * 校验 API 就绪上报（可选）。
     *
     * 【为何需要】父级 NewCooperate 通过 `ref` 拿本组件实例的路径会被 SFormItem 的
     * SlotComponent 破坏——它用 `cloneElement(children, { ref })` 注入自己的 elementRef
     * （用于 watchChildFormValidate 订阅），**覆盖**了父级传在子元素上的 ref，导致
     * 父级 ref.current 恒为 null（表现为「提交时合作内容既不校验也不滚动」且静默无报错）。
     * 故改为组件主动上报同一份 API，父级用 ref 存引用即可（不触发渲染）。
     */
    onApiReady?: (api: { validateFields: () => Promise<void>; validateAndLocate: () => boolean }) => void;
}

    
type ImportMode = 'overwrite' | 'append';

const CooperationFormList = forwardRef(function (props: CooperationFormListProps, ref) {
    const [dataList, setDataList] = useControllableValue<CooperationValueItem[]>(props, {
        defaultValue: []
    })
    const {
        category_id,
        view = false,
        hasKPI,
        kpiTableProps = {},
        depth, addable = true,
        deletable = true,
        editType = 'default',
        demand_supplier_quotes = [],
        detail,
        onImportSuccess,
        schema,
        tabsModel,
        updateReleaseTime,
        releaseTime,
        readonly = false,
        showModel = 'panel',
        onApiReady,
    } = props
    const [activeKeys, setActiveKeys] = useState<number[]>(tabsModel ? ALL_ACTIVE_KEYS : DEFAULT_ACTIVE_KEYS);
    const [activeTabKey, setActiveTabKey] = useState('0');

    // 展示模式运行时切换：初始取父级 showModel prop，用顶部分段控件可切换到表格模式（不新增容器实体）
    const [showModelState, setShowModelState] = useState<'panel' | 'table'>(showModel);
    /**
     * 实际生效的展示模式：非结构化单据恒走折叠面板，不进表格模式。
     *
     * 【为何在渲染期派生，而不是去约束 useState 的初值】`detail` 可能晚于首帧到达
     * （拆单抽屉的 detail 来自 store，且 SplitForm 以 `key={splitDraftId || demandDetail?.id}`
     * 重挂载）。若把 `detail?.structured` 写进初值，首帧取到 undefined 就会把模式
     * **永久冻结**在 panel，结构化拆单再也进不了表格模式。派生值随 detail 到达自动修正，无此风险。
     *
     * 【为何非结构化必须挡在这里】切换控件本身也以 `detail?.structured` 为门，
     * 非结构化时控件被隐藏；若此处不挡，调用方（拆单 SplitForm 无条件传 showModel='table'）
     * 仍会渲染表格，用户既看不到切换控件、也无法切回折叠面板。
     */
    const effectiveShowModel = detail?.structured ? showModelState : 'panel';
    /**
     * 【钉在左侧】已钉住的属性列 code 集合，**由本组件持有，而不是 CooperationTableList 内部 state**。
     *
     * 原因：切到「卡片（折叠面板）」时 CooperationTableList 会被卸载（下方渲染分支二选一），
     * 内部 state 随之丢失，切回「列表（表格）」时钉住全部消失（#46364）。
     * 放在这里即可跨模式切换存活。togglePin 用 useMemoizedFn 保证 identity 稳定，
     * 不影响业务 columns 的 memo 链（其依赖数组本来就含 pinnedCodes / togglePin，形状不变）。
     *
     * 【持久化】同一浏览器再次进入**相同需求类型**时自动恢复钉住，故初值直接从 localStorage
     * 按 `category_id`（l3 需求类型）读入；用户在该类型内的每次调整由 togglePin 覆写记录。
     * 详见 ./pinnedColumnsStorage.ts（含「为何按需求类型分别记」「为何存 code 不存下标」）。
     */
    const [pinnedCodes, setPinnedCodes] = useState<string[]>(() => readPinnedCodes(category_id));
    /**
     * 需求类型变了 → 换成该类型的记录（模板不同，钉住集合各记各的）。
     *
     * ⚠️ 用 ref 跳过首次执行：初值已由上面的 useState 初始化读入，这里再 set 一次会多一轮
     * 渲染（本表格对渲染次数敏感，columns → bedrockColumns 是三级 memo 链）。
     * 用户在当前类型内的调整由 togglePin 即时落盘，不会被本 effect 覆盖（它只在 category_id 变化时跑）。
     */
    const pinnedCategoryRef = useRef<number | undefined>(category_id);
    useEffect(() => {
        if (pinnedCategoryRef.current === category_id) return;
        pinnedCategoryRef.current = category_id;
        setPinnedCodes(readPinnedCodes(category_id));
    }, [category_id]);
    /**
     * 钉住/取消钉住：先算新集合（用 useMemoizedFn 拿到的永远是最新一次渲染的 pinnedCodes），
     * 再**按当前需求类型覆盖记录** —— 需求明确「用户在当前需求类型有调整需重新记录」。
     */
    const togglePin = useMemoizedFn((code: string) => {
        const next = pinnedCodes.includes(code) ? pinnedCodes.filter((c) => c !== code) : [...pinnedCodes, code];
        setPinnedCodes(next);
        savePinnedCodes(category_id, next);
    });

    /**
     * 「发布时间」**初始化后一次性补填**。
     *
     * 【为什么需要】新建拆单时第一条合作内容是 `NewCooperate` 的
     * `planInfo?.cooperate_accounts || [{}]` 兜底造的**空壳行**——它不在 `planInfo` 里，
     * 于是所有写 `planInfo` 的路径（T1 改执行日期 / T2 新增行 / 带出补填）都够不到它；
     * 且一级单执行日期是 `form.setFieldsValue()` 程序化写入的（不触发 onValuesChange），
     * T1 也不会被唤醒。本组件是**唯一同时握有 schema、合作内容行与「执行日期」值**的一层，
     * 故在此就地补：先给没有属性集的行补出属性集（否则连 RELEASE_TIME 属性项都不存在），
     * 再按执行日期填空。
     *
     * 【就绪条件】schema 已有属性集 且 有合作内容行 且 非 view 且 执行日期是合法区间 ——
     * 任一不满足就**不写不标记**，等依赖变化后重试（避免数据没到时空跑）。
     *
     * 【只补一次】用 ref 兜住：之后用户改执行日期走 T1、新增行走 T2，各路径互不干扰；
     * 也避免与用户的后续编辑打架。写回走 `setDataList`（本组件既有受控通道），
     * 无变更时 `fillReleaseTimeAfterInit` 返回原引用 → 这里跳过 setState，保证幂等、不产生多余渲染。
     */
    const releaseTimeFilledRef = useRef(false);
    useEffect(() => {
        if (view || releaseTimeFilledRef.current) return;
        const hasSchema = !!(schema?.standards?.length || schema?.properties?.length);
        if (!hasSchema || !dataList?.length) return;
        if (!Array.isArray(releaseTime) || releaseTime.length !== 2) return;
        releaseTimeFilledRef.current = true;
        const next = fillReleaseTimeAfterInit(dataList as Record<string, any>[], releaseTime, schema);
        if (next !== dataList) {
            setDataList(next as CooperationValueItem[]);
        }
    }, [dataList, schema, releaseTime, view]);

    const [form] = Form.useForm();

    // 表格模式：CooperationTableList 的 ref，用于桥接其内部 ProTableListForm 的 validateFields
    const cooperationTableRef = useRef<CooperationTableListHandle>(null);

    // 外部数据变化，更新本地数据
    const [localDataList, setLocalDataList] = useState([])
    useEffect(() => {
        if (isEqual(dataList, localDataList)) {
            return
        } else {
            form.setFieldsValue({
                list: dataList
            })
        }
    }, [dataList, localDataList]);

    /**
     * 对外校验 API：同时经 `ref` 暴露（正常场景）与 `onApiReady` 上报
     * （ref 被 SFormItem/SlotComponent 的 cloneElement 覆盖时的兜底，见 props.onApiReady 注释）。
     */
    const cooperateApi = useMemo(() => ({
        validateFields: () => {
            // 表格模式：桥接 CooperationTableList 内部 ProTableListForm 的校验（遇错滚动定位）
            if (effectiveShowModel === 'table') {
                return cooperationTableRef.current?.validateFields?.() ?? Promise.resolve();
            }
            // 缓执行，解决动态校验逻辑，导致新规则无法及时生效问题
            return new Promise<void>((resolve, reject) => {
                setTimeout(() => {
                    form.clearValidate();
                    form.validateFields()
                        .then(() => {
                            resolve(undefined)
                        })
                        .catch((error) => {
                            return reject(undefined)
                        })
                }, 10)
            })
        },
        /**
         * 同步校验：供「提交当前拆单」等同步判定场景，以及底部「校验合作内容」按钮使用。
         * @returns 是否存在错误（true = 有错；表格模式下此时已滚动定位到第一个出错单元格）
         */
        validateAndLocate: () => {
            // 表格模式：桥接 CooperationTableList → ProTableListForm.validateAndLocate
            if (effectiveShowModel === 'table') {
                return cooperationTableRef.current?.validateAndLocate?.() ?? false;
            }
            // 折叠模式：校验由 NewCooperate 自身表单链路承担，此处不做同步拦截（保持原行为）
            return false;
        },
    }), [effectiveShowModel]);

    useImperativeHandle(ref, () => cooperateApi, [cooperateApi]);

    // 上报 API 供父级（经 ref 拿不到实例时）调用；存进 ref 不触发渲染，不会造成更新循环
    useEffect(() => {
        onApiReady?.(cooperateApi);
    }, [onApiReady, cooperateApi]);


    const { openImportFileModal, closeImportFileModal } = useImportFileModal({});

    const handleImportCooperate = useMemoizedFn((mode: ImportMode) => {
        const importType = `demand_cooperate_planner_${category_id}`;
        openImportFileModal({
            importType,
            importIsMemory: false,
            customDownloadRender: () => (
                <div>
                    <Space size='xSmall' split={<Divider />}>
                        <ExportFilePollingBtn
                            exportKey='DEMANDS_EXPORT_COOPERATE_DATA'
                            params={{
                                l3_category_id: category_id,
                                cooperate_account_dto_list: dataList?.map?.((item, index) => {
                                    return { ...item, index_id: item?.index_id ?? index + 1 }
                                }),
                            }}
                            buttonProps={{
                                icon: <SignOut />,
                                type: 'link',
                            }}
                            buttonText='导出当前数据'
                        />
                        <Button
                            icon={<Download />}
                            type='link'
                            onClick={() => {
                                window.open(`/api/v2/java_proxy/common/data/${importType}/import-template`)
                            }}
                        >
                            下载空模板
                        </Button>
                    </Space>
                    <div className="download-span fs-12 m-l-4">
                        (上传前请先下载Excel模板并按照模板格式编辑文件，或先导出当前数据后再导入)
                    </div>
                </div>
            ),
            uploadFinishedCallback: (res) => {
                // 有错误时，不进行数据更新
                if (res?.data?.fail_count > 0) return;
                if (res.code === 1 && res.data?.data?.length) {
                    closeImportFileModal();
                    const list = res.data?.data?.map(v => v?.demand_cooperate_account_dto);
                    if (mode === 'overwrite') {
                        Message.success(`导入覆盖成功 ${list.length} 条数据`)
                        setDataList(list)
                    } else {
                        Message.success(`导入添加成功 ${list.length} 条数据`)
                        setDataList([...dataList, ...list])
                    }
                    onImportSuccess?.();
                }
            },
        })
    })

    const onFormChange = useMemoizedFn((changedValues, allValues) => {
        setDataList(allValues.list)
        setLocalDataList(allValues.list)
    })
    const isSingle = dataList?.length <= 1;

    const handleApplyToTheWholeOrder = (key, value) => {
        const newDataList = dataList.map((item, index) => ({
            ...item,
            [key]: value
        }))
        setDataList(newDataList);
        Message.success('应用成功，已覆盖填写到全部合作内容')
    }



    // 选择字段应用至整单（覆盖逻辑下沉至 applyToWholeOrder.ts，折叠模式/表格模式共用）
    const handleApplyValuesToTheWholeOrder = useMemoizedFn(({
        coverMode,
        coverKeys,
        currentIndex,
    }: {
        coverMode: 'retain' | 'overwrite',
        coverKeys: string[],
        currentIndex: number,
    }) => {
        const newDataList = applyValuesToRows(dataList, currentIndex, { coverMode, coverKeys });

        setDataList(newDataList as CooperationValueItem[]);
        Message.success('应用成功')
    })

    return (<Form
        className={cx('mpc-common-cooperate-form-list')}
        form={form}
        onValuesChange={onFormChange}
        initialValues={{
            list: [{}]
        }}
        style={{ width: '100%' }}
    >
        <Form.List
            name="list"
        >
            {(fields, { add, remove }, { errors }) => {
                const hasError = errors && !!errors.length;
                return (
                    <>
                      {!view && addable && detail?.structured && (
                        /* 视图切换与右侧操作入口**同一行**（走查 #31629：「全屏下这边的按钮位置错误」）。
                           此前右侧按钮挂 `.import-file-btn`（`position:absolute; right:0; top:-40px`），
                           靠「向上浮一行」贴到上一行去；全屏模式下那一行是页面级「合作内容」标题，
                           于是「添加达人 / 批量导入合作内容」跑到标题行、与「列表|卡片」分了家。
                           改为一行 flex 两端对齐，不再依赖绝对定位（也不再需要 relative 包裹层）。
                           下间距 16px：`mb-4`（本仓 Tailwind 间距 4px 基准 → key N = 4N px），
                           走查要求由 8px 增至 16px；此处不用旧工具类 `m-b-8`（那是 1px 基准的 8px）。 */
                        <div className="flex items-center justify-between gap-4 mb-4">
                            {/* 展示层切换：panel=折叠面板 / table=表格模式。仅做属性切换，非真正 tab，无容器实体 */}
                            <Radio.Group
                                fill
                                value={effectiveShowModel}
                                onChange={(e) => setShowModelState(e.target.value as 'panel' | 'table')}
                            >
                                {/* 表格在前：拆单场景以表格模式为优先展示（默认值见 showModel prop） */}
                                <Radio.Button value="table">列表</Radio.Button>
                                <Radio.Button value="panel">卡片</Radio.Button>
                            </Radio.Group>
                            <Space size={8}>
                            {ALLOW_SELECT_KOL_CATEGORY_ID.includes(category_id?.toString()) && (
                                <Dropdown
                                    overlay={(
                                        <Menu
                                            onClick={({ key }) => {
                                                const mode = key as ImportMode;
                                                StarSelectDrawer.show({
                                                    dataType: 'cooperate',
                                                    l3_category_id: category_id,
                                                    manualAddable: false,
                                                    title: '选择在库达人',
                                                    onOk: ({ contentData }) => {
                                                        if (contentData?.length) {
                                                            if (mode === 'overwrite') {
                                                                setDataList(contentData);
                                                                Message.success('达人覆盖添加成功，已清空原数据并导入新达人')
                                                            } else {
                                                                if (dataList.length === 1 && dataList[0]?.property_values?.every((item) => !item.value || item?.value?.length === 0)) {
                                                                    setDataList(contentData);
                                                                } else {
                                                                    setDataList([...dataList, ...contentData]);
                                                                }
                                                                Message.success('达人添加成功，可通过「批量导入」/「字段应用至整单」功能快速补充剩余字段信息')
                                                            }
                                                        } else {
                                                            Message.info('没有有效数据')
                                                        }
                                                    }
                                                })
                                            }}
                                        >
                                            <Menu.Item key="overwrite">
                                                清空覆盖导入
                                            </Menu.Item>
                                            <Menu.Item key="append">
                                                添加导入
                                            </Menu.Item>
                                        </Menu>
                                    )}
                                >
                                    <Button
                                        size='medium'
                                        type="checked-neutral"
                                        icon={<Plus />}
                                        suffixIcon={<ChevronDown className="rock-dropdown-down-arrow-icon" />}
                                    >
                                        添加达人
                                    </Button>
                                </Dropdown>
                            )}
                            <Dropdown
                                overlay={(
                                    <Menu
                                        onClick={({ key }) => handleImportCooperate(key as ImportMode)}
                                    >
                                        <Menu.Item key="overwrite">
                                          清空覆盖导入
                                        </Menu.Item>
                                        <Menu.Item key="append">
                                           添加导入
                                        </Menu.Item>
                                    </Menu>
                                )}
                            >
                                <Button
                                    type="checked-neutral"
                                    icon={<SignIn />}
                                    suffixIcon={<ChevronDown className="rock-dropdown-down-arrow-icon" />}
                                >
                                    批量导入合作内容
                                </Button>
                            </Dropdown>
                            </Space>
                        </div>
                      )}
                    {effectiveShowModel === 'table' ? (
                        <CooperationTableList
                            ref={cooperationTableRef}
                            value={dataList as any}
                            onChange={(next) => setDataList(next as CooperationValueItem[])}
                            schema={schema}
                            structuredVersion={getInnerStructuredSersion(detail, view)}
                            view={view}
                            // 是否可添加合作内容：表格模式的子数组「应用至整单」与卡片模式共用同一条门控
                            // `!view && addable && structured`（策划拆单预览态传 addable={false} → 不显示）
                            addable={addable}
                            category_id={category_id}
                            // 是否结构化单据：need_ai 列的门控之一（对齐折叠面板 `!view && detail?.structured && getCooperateAIRequired(category_id)`）
                            structured={detail?.structured}
                            detailId={kpiTableProps?.detailId}
                            platforms={['MPC']}
                            moreKpi={kpiTableProps?.moreKpi}
                            groupType={kpiTableProps?.groupType}
                            detailState={kpiTableProps?.detailState}
                            colKeys={kpiTableProps?.colKeys}
                            productArea={kpiTableProps?.product_area}
                            need_actual_execution={kpiTableProps?.need_actual_execution}
                            disableKpiCodeEdit={kpiTableProps?.disableKpiCodeEdit}
                            disableExpectValueEdit={kpiTableProps?.disableExpectValueEdit}
                            // 考核指标整体必填（面板模式经 kpiTableProps.required 注入 NewCooperate 的 cooperateKpiRequired 判定）
                            kpiRequired={kpiTableProps?.required}
                            // 无考核指标的需求类型不渲染指标列（对齐面板 KPITable 面板的 hide: !hasKPI）
                            hasKPI={hasKPI}
                            // 拆单「已有执行周期」纬度：隐藏「+添加指标」/ 禁用指标行删除
                            disableAdd={kpiTableProps?.disableAdd}
                            disableDelete={kpiTableProps?.disableDelete}
                            // 「字段应用至整单」的权益可应用条件（对齐面板 hideOptions）
                            structured_version={detail?.structured_version}
                            state={detail?.state}
                            need_cooperate_account_supplier_settle_price={detail?.need_cooperate_account_supplier_settle_price}
                            // 新增合作内容后重算发布时间（对齐面板 add 后 500ms 调 updateReleaseTime）
                            updateReleaseTime={updateReleaseTime}
                            // 钉在左侧的集合由本组件持有，跨「列表/卡片」切换存活（#46364）
                            pinnedCodes={pinnedCodes}
                            togglePin={togglePin}
                        />
                    ) : (
                      <>
                        {tabsModel && <Tabs activeKey={activeTabKey} type="card" onChange={(v) => {setActiveTabKey(v)}} className='m-b-8'>
                            {
                                fields.map((field, index) => {
                                    return <Tabs.TabPane tab={`合作内容${orderNum(dataList?.[index]?.$orderIndex, index)}`} key={index + ''}></Tabs.TabPane>
                                })
                            }
                        </Tabs>}
                        {fields.map((field, index) => {
                            const { key, name, ...fieldRest } = field;
                            const fieldConfig = (fieldType: string) => {
                                return {
                                    formItemKey: [name, fieldType],
                                    name: [name, fieldType],
                                    fieldKey: [key, fieldType],
                                    view,
                                    ...fieldRest
                                }
                            }

                            const { delay_grade, delay_label, $orderIndex, index_id } = dataList?.[index] || {};
                            const cooperate_account_id = dataList?.[index]?.id;
                            const demand_supplier_quote = demand_supplier_quotes?.find?.(v => v.cooperate_account_id === cooperate_account_id);
                            const title = `合作内容${orderNum($orderIndex, index)}`

                          if (tabsModel && index !== Number(activeTabKey)) {
                                return null;
                            }


                            return (
                                <Collapse
                                    // defaultActiveKey={[1]}
                                    activeKey={activeKeys.includes(index) ? [1] : undefined}
                                    key={index}
                                    onChange={(key) => {
                                        setActiveKeys(activeKeys.includes(index) ? activeKeys.filter((i) => i !== index) : [...activeKeys, index])
                                    }}
                                >
                                    <CollapseTablePanel
                                        className={OvertimeTag.conf?.[delay_grade]?.panelHeaderbgClassName}
                                        // showArrow={false}
                                        style={{ width: '100%' }}
                                        header={<div
                                            className={cx('inner-header')}
                                        >
                                            <div>
                                                <Space size={4}>
                                                    <span>{title}</span>
                                                    {dataList[index]?.need_ai && <Tag className='font-normal' color="orange" size="xSmall" text={'需包含AI技术'}></Tag>}
                                                     {depth === 2 && !props.inPlanning && <ActualExecutionTag value={dataList[index]?.need_actual_execution} />}
                                                    {!props.inPlanning && <TravelTag
                                                        value={demand_supplier_quote?.contain_travel_expenses_reimbursement}
                                                        need_actual_execution={demand_supplier_quote?.need_actual_execution}
                                                        detail={detail}
                                                    />}
                                                    {!props.inPlanning && <OvertimeTag delay_grade={delay_grade} delay_label={delay_label} className='' />}
                                                </Space>
                                            </div>
                                            {!view && <Space
                                                size={0}
                                            >
                                                {
                                                    addable && detail?.structured && 
                                                    <DropdownListApplyToTheWholeOrder
                                                        schema={schema}
                                                        hideOptions={[
                                                                {
                                                        name: 'kpi_values',
                                                        hide: !hasKPI || (detail?.need_cooperate_account_supplier_settle_price && dataList[index]?.need_actual_execution === false),
                                                            }, {
                                                        name: 'necessary_rights',
                                                    hide: detail?.structured_version < 1 && !['pending_design','underway'].includes(detail?.state),
                                                            }, {
                                                        name: 'expect_rights',
                                                    hide: detail?.structured_version < 1 && !['pending_design','underway'].includes(detail?.state),
                                                            }]}
                                                        onOk={(applyConfig) => handleApplyValuesToTheWholeOrder({
                                                            coverMode: applyConfig.coverMode,
                                                            coverKeys: applyConfig.coverKeys,
                                                            currentIndex: index,
                                                        })} />
                                                }
                                                {addable &&
                                                    <Button type="text-subtle" size="xSmall" icon={<CopyNewAdd />}
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                const listData = form.getFieldValue('list')
                                                                const copyData = JSON.parse(JSON.stringify(listData[index]))
                                                                add(copyData, index + 1)
                                                            }}
                                                    >
                                                        复制合作内容
                                                    </Button>
                                                }

                                                {deletable &&
                                                    <Button disabled={isSingle} type="text-subtle" size="xSmall" icon={<Delete />}
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                remove(field.name);
                                                            }}
                                                            danger
                                                    >
                                                            删除
                                                    </Button>
                                                }
                                            </Space>}
                                            {detail?.need_cooperate_account_supplier_settle_price && !props.inPlanning && <div>
                                                <span className={cx('settle-title')}>最终结算金额：</span>
                                                <CurrencyType value={dataList?.[index]?.$supplier_settle_price} view showAmountOrder={false} />
                                            </div>}
                                        </div>}
                                        key={1}>
                                        {!view && detail?.structured &&  getCooperateAIRequired(category_id) && <FormItemDescriptions
                                            lightBorder
                                            className={cx('need_ai_item')}
                                            column={2}
                                            items={[
                                                {
                                                    ...fieldConfig('need_ai'),
                                                    label: '是否需包含AI',
                                                    required: true,
                                                    view: false,
                                                    span: 2,
                                                    componentClass: 'radio',
                                                    elementProps: {
                                                        options: [
                                                            { name: '是', value: true },
                                                            { name: '否', value: false },
                                                        ],
                                                    },
                                                },
                                            ]}
                                        />}
                                        {!view && detail?.structured && <SFromItem
                                            {...fieldConfig('property_values')}
                                            showLabel={false}
                                            validateTrigger={false}
                                            watchChildFormValidate={true}
                                            className={cx('property_values_from_item')}
                                            render={() => {
                                                return <CooperationPropertyForm
                                                    view={view}
                                                    schema={schema}
                                                    editType={props.editType}
                                                    structuredVersion={getInnerStructuredSersion(detail, view)}
                                                />
                                            }}
                                        />}
                                        {detail?.structured && <FormItemDescriptions
                                            lightBorder
                                            className={cx('collapse-content-table')}
                                            column={2}
                                            items={[
                                                {
                                                    ...fieldConfig('property_values'),
                                                    label: '合作内容',
                                                    validateTrigger:false,
                                                    watchChildFormValidate: true,
                                                    span: 2,
                                                    hide: !view || !dataList?.[index]?.property_values?.length,
                                                    render:() => {
                                                        return <CooperationDetails index_id={orderNum($orderIndex, index)} need_ai={dataList?.[index]?.need_ai}/>
                                                    }
                                                },
                                                {
                                                    ...fieldConfig('kpi_values'),
                                                    label: '考核指标',
                                                    view: readonly ? true : (() => {
                                                        // 判断是否有编辑权限,单个编辑，否则无法校验
                                                        const hasColEdit = kpiTableProps?.colKeys?.some?.((n) => n.view === false)
                                                        if (hasColEdit) return false;
                                                        return view
                                                    })(),
                                                    required: !!kpiTableProps?.required,
                                                    span: 2,
                                                    watchChildFormValidate: true,
                                                    validateTrigger: false,
                                                    hide: !hasKPI || (detail?.need_cooperate_account_supplier_settle_price && dataList[index]?.need_actual_execution === false),
                                                    render: (renderData, itemProps) => {
                                                        // 是否实际执行为否则KPI不可编辑
                                                        const kpiView = dataList[index]?.need_actual_execution === false ? true : (view || readonly);
                                                        return <KPITable
                                                            need_actual_execution={dataList[index]?.need_actual_execution}
                                                            view={kpiView}
                                                            required={false}
                                                            platforms={['MPC']}
                                                            emptyRender={() => {
                                                                return <div>--</div>
                                                            }}
                                                            $supplier_settle_price={dataList[index]?.$supplier_settle_price}
                                                            cooperate_account_id={cooperate_account_id}
                                                            need_cooperate_account_supplier_settle_price={detail?.need_cooperate_account_supplier_settle_price}
                                                            applyToTheWholeOrder={!view && addable && detail?.structured}
                                                            handleApplyToTheWholeOrder={(value) => handleApplyToTheWholeOrder('kpi_values', value)}
                                                            {...kpiTableProps}
                                                        />
                                                    }
                                                },
                                                {
                                                    ...fieldConfig('necessary_rights'),
                                                    label: '必要权益',
                                                    span: 2,
                                                    hide: detail?.structured_version < 1 && !['pending_design','underway'].includes(detail?.state),
                                                    required: true,
                                                    watchChildFormValidate: true,
                                                    validateTrigger: false,
                                                    render: () => {
                                                        return <EquityFormTable
                                                            key='necessary_rights' 
                                                            formItemKey='necessary_rights' 
                                                            detail={detail} 
                                                            view={view || readonly} 
                                                            inPlanning={props.inPlanning}
                                                            rightsList={schema?.necessary_rights_list}
                                                            applyToTheWholeOrder={!view && addable && detail?.structured}
                                                            handleApplyToTheWholeOrder={(value) => handleApplyToTheWholeOrder('necessary_rights', value)}
                                                        />
                                                    }
                                                },
                                                {
                                                    ...fieldConfig(getFormItemKey(detail, {inPlanning: props.inPlanning, view})),
                                                    label: getExpectRightsLabel(getFormItemKey(detail, {inPlanning: props.inPlanning, view})),
                                                    span: 2,
                                                    hide: detail?.structured_version < 1 && !['pending_design','underway'].includes(detail?.state),
                                                    watchChildFormValidate: true,
                                                    validateTrigger: false,
                                                    render: () => {
                                                        return <EquityFormTable
                                                                key='expect_rights'
                                                                formItemKey={getFormItemKey(detail, {inPlanning: props.inPlanning, view})}
                                                                detail={detail}
                                                                inPlanning={props.inPlanning}
                                                                view={view || readonly}
                                                                tableRequired={false}
                                                                rightsList={schema?.expect_rights_list}
                                                                applyToTheWholeOrder={!view && addable && detail?.structured}
                                                                handleApplyToTheWholeOrder={(value) => handleApplyToTheWholeOrder(getFormItemKey(detail, {inPlanning: props.inPlanning, view}), value)}
                                                            />
                                                    }
                                                },
                                                {
                                                    ...fieldConfig('remark'),
                                                    label: '备注',
                                                    required: false,
                                                    componentClass: "textarea",
                                                    span: 2,
                                                    elementProps: {
                                                        maxLength: 500,
                                                    }
                                                },
                                                {
                                                    ...fieldConfig('discount_quote_price_with_tax'),
                                                    label: '报价金额-变更后(含税 + 服务费)',
                                                    span: 1,
                                                    hide: !detail?.need_cooperate_account_supplier_settle_price || props.inPlanning,
                                                    render: () => {
                                                        return <PriceDetailModal
                                                            demand_supplier_quote={demand_supplier_quote}
                                                            detail={detail}
                                                            orderNum={orderNum($orderIndex, index)}
                                                        />
                                                    }
                                                },
                                                {
                                                    ...fieldConfig('$supplier_settle_price'),
                                                    label: '最终结算金额(含税 + 服务费)',
                                                    span: 1,
                                                    componentClass: 'currencyType',
                                                    view: $sType === 'inner' || readonly ||
                                                     dataList[index]?.need_actual_execution === false,
                                                    required: true,
                                                    hide: !detail?.need_cooperate_account_supplier_settle_price || props.inPlanning,
                                                    elementProps: {
                                                        selectDisabled: true,
                                                        // max: demand_supplier_quote?.discount_quote_price_with_tax,
                                                        // formatter: undefined,
                                                    },
                                                    rules:
                                                        [
                                                            {
                                                                message: '金额需 ≤ 报价金额-变更后（含税+服务费）',
                                                                validator: function (_, value) {
                                                                    return new Promise((resolve, reject) => {
                                                                        if (value.value > demand_supplier_quote?.discount_quote_price_with_tax) {
                                                                            reject(new Error('金额需 ≤ 报价金额-变更后（含税+服务费）'));
                                                                        } else {
                                                                            resolve('');
                                                                        }
                                                                    });
                                                                },
                                                            }
                                                        ]

                                                },
                                                {
                                                    ...fieldConfig('case_close_detail'),
                                                    label: '结案明细',
                                                    span: 2,
                                                    view: true,
                                                    hide: !(detail?.need_structured_settle_report && (detail?.depth === 2) && demand_supplier_quote?.need_actual_execution && !props.inPlanning),
                                                    elementProps: ({ optionMap, detailData }) => {
                                                        return {
                                                            showComponent: (value) => {
                                                                return (
                                                                    <ClosingReportBtn detail={detail} view={view || readonly} cooperateAccountId={cooperate_account_id} />
                                                                )
                                                            }
                                                        }
                                                    },
                                                    ...bindAiReviewResultToFormItem(`quoteSettleReport:${index_id}`, {
                                                        detailData: detail,
                                                    }),
                                                },
                                                {
                                                    ...fieldConfig('case_close_report_cooperate_account_attachments'),
                                                    label: '结案报告 (差旅、餐补)',
                                                    span: 2,
                                                    required: !!demand_supplier_quote?.contain_travel_expenses_reimbursement,
                                                    view: readonly ? true : detail?.state !== 'making',
                                                    hide: (detail?.depth !== 2 || props.inPlanning || !detail?.$contentStructured || !detail?.is_screenshot_done)
                                                        ? true : !demand_supplier_quote?.need_actual_execution,
                                                    componentClass: 'upload',
                                                    inputExtra: !!demand_supplier_quote?.contain_travel_expenses_reimbursement && detail?.state === 'making' && (
                                                        <span className={cx('case-close-report-tip')}>注：当前合作内容包含差旅餐饮等结算项，需上传对应结案证明，以供实报实销结算。</span>
                                                    ),
                                                    ...bindAiReviewResultToFormItem(`caseCloseReportCooperateAccountAttachments:${index_id}`, {
                                                        detailData: detail,
                                                    }, { showGroups: true }),
                                                },
                                            ].filter(n => !n.hide)}
                                        />}
                                        {!detail?.structured && <NoStructuredCooperation fieldConfig={fieldConfig}/>}
                                    </CollapseTablePanel>
                                </Collapse>
                            );
                        })}
                        {!view && addable && <>
                            <Button type="checked" icon={<Plus />}
                                    onClick={() => {
                                        add({});
                                        // 新增合作内容后带出「执行日期」→ 该行的「发布时间」（触发点 T2）。
                                        // ⚠️ 必须延迟：新行要先经 onChange → planInfo 落地，带出才有对象可写；
                                        // 与表格模式共用同一延迟常量，保证两模式时序一致。
                                        setTimeout(() => updateReleaseTime?.({}, true), ADD_ROW_RELEASE_TIME_DELAY_MS);
                                    }}
                                    // style={{width: '100%'}}
                                    size='medium'
                            >
                                添加合作内容
                            </Button>
                        </>}
                      </>
                    )}
                    </>
                );
            }}
        </Form.List>
    </Form>)
})

export default CooperationFormList;
